import useSignedUrlToGetFile from "../../hooks/useSignedUrlToGetFile";
import Button from "../Button";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useMutation } from "@apollo/client";
import {
  DeleteDocumentDocument,
  DocumentFragment,
  DocumentsByUserOrganizationIdDocument,
  EventDetailsDocument,
  OrganizationDocumentsDocument,
  ProjectDetailsDocument,
} from "../../__generated__/graphql";
import toast from "react-hot-toast";
import { useUser } from "../../contexts/UserContext/hooks/useUser";
import { useSearchParams } from "react-router";
import { documentTypeStringToDocumentType } from "../../lib/utils";
import { useMemo } from "react";
import DocumentCarousel from "./DocumentCarousel";

export default function DocumentPreview({
  documentToPreview,
  onDelete,
}: {
  documentToPreview: DocumentFragment;
  onDelete: () => void;
}) {
  const [searchParams] = useSearchParams();
  const documentType = searchParams.get("documentType");

  const { currentOrganization } = useUser();
  const {
    bucketKey,
    ownerOrganizationId,
    id,
    title,
    documentSharings,
    documentCarousel,
  } = documentToPreview;

  // Sort carousel items by order
  const sortedCarouselItems = useMemo(() => {
    if (!documentCarousel || documentCarousel.length === 0) return null;
    return [...documentCarousel].sort((a, b) => a.order - b.order);
  }, [documentCarousel]);

  // Use carousel if available, otherwise use main document
  const isCarousel = sortedCarouselItems && sortedCarouselItems.length > 0;

  const {
    data: signedUrlData,
    loading,
    error,
  } = useSignedUrlToGetFile(isCarousel ? null : bucketKey);
  const fileUrl = signedUrlData?.signedUrlToGetFile;

  const [deleteDocument, { loading: deleteLoading }] = useMutation(
    DeleteDocumentDocument
  );

  const isOwner = ownerOrganizationId === currentOrganization?.id;
  const isDocumentSent =
    documentToPreview.originalSenderOrganizationId !== null ||
    documentToPreview.originalSenderUserOrganization !== null;

  const handleDelete = async () => {
    try {
      const response = confirm("Voulez-vous vraiment supprimer ce document ?");

      if (!response) return;

      await deleteDocument({
        variables: { documentId: id },
        refetchQueries: [
          {
            query: OrganizationDocumentsDocument,
            variables: {
              filter: {
                type: documentType
                  ? documentTypeStringToDocumentType[documentType]
                  : undefined,
              },
            },
          },
          ...(documentToPreview.projectId
            ? [
                {
                  query: ProjectDetailsDocument,
                  variables: { projectId: documentToPreview.projectId },
                },
              ]
            : []),
          ...(documentToPreview.eventId
            ? [
                {
                  query: EventDetailsDocument,
                  variables: { eventId: documentToPreview.eventId },
                },
              ]
            : []),
          ...(documentSharings?.map((documentSharing) => ({
            query: DocumentsByUserOrganizationIdDocument,
            variables: {
              userOrganizationId:
                documentSharing.recipientPublicUserOrganization.id,
            },
          })) || []),
        ],
      });

      toast.success("Document supprimé avec succès");
      onDelete();
    } catch (err) {
      console.error("Suppression du document impossible:", err);
      toast.error("Suppression du document impossible");
    }
  };

  const deleteButton = () => {
    if (!isOwner || isDocumentSent) {
      return null;
    }

    return (
      <Button
        variant="secondary"
        isLoading={deleteLoading}
        className="flex gap-2 items-center text-red-400 hover:text-red-400/80"
        onClick={handleDelete}
      >
        <TrashIcon className="w-4 h-4" />
        Supprimer
      </Button>
    );
  };

  // If using carousel, render carousel preview
  if (isCarousel && sortedCarouselItems) {
    return (
      <div className="flex flex-col w-full h-full rounded-md overflow-hidden">
        <div className="py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {sortedCarouselItems.length > 1 && (
              <span className="text-sm text-secondary">
                {sortedCarouselItems.length} document
                {sortedCarouselItems.length > 1 ? "s" : ""}
              </span>
            )}
          </div>

          {deleteButton()}
        </div>

        <div className="flex-1 flex items-center justify-center w-full h-full min-h-0 relative">
          <DocumentCarousel carouselItems={sortedCarouselItems} title={title} />
        </div>
      </div>
    );
  }

  // Regular single file preview
  if (loading) return <div>Récupération du document...</div>;
  if (error) return <div>Error: {error.message}</div>;

  if (!fileUrl) return null;

  const previewDisplay = () => {
    // TODO: it seems that we can't display images with img and make the download works
    if (documentToPreview.mimeType.startsWith("video/")) {
      return (
        <video
          src={fileUrl}
          style={{ width: "100%", height: "600px" }}
          className="rounded-md"
          controls
        />
      );
    } else if (documentToPreview.mimeType.startsWith("image/")) {
      return (
        <img
          src={fileUrl}
          alt={title}
          className="w-full h-full object-contain rounded-md"
        />
      );
    } else {
      return (
        <iframe
          src={fileUrl}
          style={{
            width: "100%",
            height: "600px",
          }}
        />
      );
    }
  };

  return (
    <div className="flex flex-col w-full h-full rounded-md overflow-hidden">
      <div className="py-2 flex">{deleteButton()}</div>

      <div className="w-full h-full rounded-md border border-input">
        {previewDisplay()}
      </div>
    </div>
  );
}
